const gridRow = {
    name: "grid-row",
    path: "/grid-row",
    title: "grid-row",
    description: "Utilities for controlling how elements are sized and placed across grid rows.",
    value: {
        "grid-row": {
            "row-span-<number>": "span <number> / span <number>",
            "row-span-full": "1 / -1",
            "row-auto": "auto",
            "row-<number>": "<number>",
            "-row-<number>": "calc(<number> * -1)",
            "row-[<value>]": "<value>",
        },
        "grid-row-start":{
            "row-start-<number>": "<number>",
            "-row-start-<number>": "calc(<number> * -1)",
            "row-start-auto": "auto",
        },
        "grid-row-end":{
            "row-end-<number>": "<number>",
            "-row-end-<number>": "calc(<number> * -1)",
            "row-end-auto": "auto",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="grid grid-flow-col grid-rows-3 gap-4">
  <div class="row-span-3 ...">01</div>
  <div class="col-span-2 ...">02</div>
  <div class="col-span-2 row-span-2 ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="grid grid-flow-col grid-rows-3 gap-4">
  <div class="row-span-2 row-start-2 ...">01</div>
  <div class="row-span-2 row-end-3 ...">02</div>
  <div class="row-start-1 row-end-4 ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="grid grid-columns-6 gap-4">
  <div class="row-[1_/_span_3] ...">01</div>
  <div class="row-[2_/_span_2] ...">02</div>
  <div class="row-start-1 row-end-4 ...">03</div>
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">row-start-<var>&lt;number&gt; or row-end-<var>&lt;number&gt;</var></code>
                   utilities like row-start-2 and row-end-3 to make an element start or end at the nth grid line:
            </p>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="col-start-1 row-start-1 grid grid-flow-col grid-rows-3 gap-4 rounded-lg text-center font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="row-span-3 grid place-content-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="col-span-2 grid place-content-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-fuchsia-400">02</div>
                    <div class="col-span-2 row-span-2 grid place-content-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
            </div>

            
            `
        },
        {
            data: `
           <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">col-start-<var>&lt;number&gt;</var>, col-end-<var>&lt;number&gt;</var></code>
                  utilities like col-start-2 and col-end-3 to make an element start or end at the nth grid line:
            </p>

             <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="grid grid-flow-col grid-rows-3 gap-4 rounded-lg text-center font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="row-span-2 row-start-2 grid place-content-center rounded-lg bg-color-pink-500 p-4 sm:p-12">01</div>
                    <div class="row-span-2 row-end-3 grid place-content-center rounded-lg bg-color-pink-300 p-4 sm:p-12 dark:bg-color-pink-800 dark:text-fuchsia-400">02</div>
                    <div class="row-start-1 row-end-4 grid place-content-center rounded-lg bg-color-pink-500 p-4 sm:p-12">03</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">Using a custom value</code>
                  Use utilities like <code class="text-color-gray-800 dark:text-color-white font-semibold">col-[<value>]</code> to set the grid column size and location based on a completely custom value:
            </p>

             <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="grid grid-flow-col grid-rows-3 gap-4 rounded-lg text-center font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="row-[1_/_span_3] grid place-content-center rounded-lg bg-color-pink-500 p-4 sm:p-12">01</div>
                    <div class="row-[2_/_span_2] grid place-content-center rounded-lg bg-color-pink-300 p-4 sm:p-12 dark:bg-color-pink-800 dark:text-fuchsia-400">02</div>
                    <div class="row-start-1 row-end-4 grid place-content-center rounded-lg bg-color-pink-500 p-4 sm:p-12">03</div>
                </div>
            </div>
            `
        },
    ]
};

export default gridRow;

<div className="sr-only col-span-2 grid-columns-3 col-span-3 col-start-2  bg-color-blue-200">

<div class="col-start-1 row-[1_/_span_3] row-[2_/_span_2] row-start-1 grid grid-flow-col grid-rows-3 gap-4 rounded-lg text-center font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="row-span-3 grid place-content-center rounded-lg bg-color-pink-500 p-4 sm:p-12">01</div>
                    <div class="col-span-2 grid place-content-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-fuchsia-400">02</div>
                    <div class="col-span-2 row-span-2 grid place-content-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
                                    <div class="row-span-2 row-start-2 grid place-content-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="row-span-2 row-end-3 grid place-content-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-fuchsia-400">02</div>
                    <div class="row-start-1 row-end-4 grid place-content-center rounded-lg bg-color-pink-500 p-4">03</div>


</div>